import { useState } from 'react'
import type { CourseMode } from '@/features/courses/types'
import { useCourseDocuments } from '../hooks/useSessions'
import type { CourseDocument } from '../types'
import { useTranslation } from 'react-i18next'

interface DocumentsSidebarProps {
    courseId: string
    mode: CourseMode
}

function StatusIcon({ status }: { status: CourseDocument['status'] }) {
    const { t } = useTranslation()

    if (status === 'ready') {
        return (
            <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: 14, height: 14, flexShrink: 0, color: 'var(--color-success)' }}
                aria-label={t('session.doc_ready')}
            >
                <path
                    d="M3 8l3.5 3.5L13 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        )
    }

    if (status === 'failed') {
        return (
            <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: 14, height: 14, flexShrink: 0, color: 'var(--color-error)' }}
                aria-label={t('session.doc_failed')}
            >
                <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                />
            </svg>
        )
    }

    return (
        <span
            aria-label={t('session.doc_processing')}
            style={{
                width: 14,
                height: 14,
                flexShrink: 0,
                borderRadius: '50%',
                border: '2px solid var(--color-primary)',
                borderTopColor: 'transparent',
                display: 'inline-block',
                animation: 'spin 0.8s linear infinite',
            }}
        />
    )
}

export function DocumentsSidebar({ courseId, mode }: DocumentsSidebarProps) {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const { data: documents, isLoading, isError } = useCourseDocuments(courseId, mode !== 'free')

    if (mode === 'free') return null

    return (
        <>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

            {/* Toggle button — visible on all screen sizes */}
            <button
                type="button"
                onClick={() => setIsOpen((v) => !v)}
                title={isOpen ? 'Masquer les documents' : 'Afficher les documents'}
                aria-expanded={isOpen}
                aria-label={isOpen ? 'Masquer les documents' : 'Afficher les documents'}
                className="shrink-0 flex items-center justify-center border-l transition-colors"
                style={{
                    width: 44,
                    minWidth: 44,
                    backgroundColor: isOpen ? 'var(--color-primary-subtle)' : 'white',
                    borderColor: '#E5E7EB',
                    color: isOpen ? 'var(--color-primary)' : '#6B7280',
                }}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    style={{ width: 16, height: 16 }}
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {/* Panel */}
            {isOpen && (
                <aside
                    className="flex flex-col border-l overflow-y-auto"
                    style={{
                        width: 220,
                        minWidth: 220,
                        backgroundColor: '#FAFAFA',
                        borderColor: '#E5E7EB',
                    }}
                >
                    <div
                        className="flex items-center justify-between px-3 py-2 border-b"
                        style={{ borderColor: '#E5E7EB' }}
                    >
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: 'var(--color-neutral-600)',
                            }}
                        >
                            {t('session.documents')}
                        </span>
                    </div>

                    <div className="flex flex-col gap-1 p-2 flex-1">
                        {isLoading && (
                            <p style={{ fontSize: '12px', color: 'var(--color-neutral-600)', padding: '8px 4px' }}>
                                {t('common.loading')}
                            </p>
                        )}

                        {(isError || (!isLoading && (!documents || documents.length === 0))) && (
                            <p style={{ fontSize: '12px', color: 'var(--color-neutral-600)', padding: '8px 4px' }}>
                                {t('session.no_documents')}
                            </p>
                        )}

                        {documents?.map((doc) => (
                            <div
                                key={doc.id}
                                className="flex items-center gap-2 rounded px-2 py-1.5"
                                style={{ minHeight: 36 }}
                            >
                                <StatusIcon status={doc.status} />
                                <span
                                    style={{
                                        fontSize: '12px',
                                        color: 'var(--color-neutral-700)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                    }}
                                    title={doc.filename}
                                >
                                    {doc.filename}
                                </span>
                            </div>
                        ))}
                    </div>
                </aside>
            )}
        </>
    )
}
